import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  getTimeline,
  deleteTimelineEntry,
  type TimelineEntry,
} from '../../features/timeline';
import { getImageUrlById } from '../../features/images';
import {
  DeleteConfirmModal,
  DashboardPageHeader,
  DashboardStatCard,
} from '../../shared/ui';
import { getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';
import TimelineModal from '../../components/dashboard/TimelineModal';
import './TimelineManagementPage.css';

const parseImageIds = (imageIdsJson: string | null | undefined): string[] => {
  if (!imageIdsJson) return [];
  try {
    const parsed = JSON.parse(imageIdsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const stripHtmlPreview = (html: string, max = 160): string => {
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};

const TimelineManagementPage: React.FC = () => {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [entries, setEntries] = useState<TimelineEntry[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<TimelineEntry | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TimelineEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const canView = !!profile && can(profile, 'timeline', 'view');
  const canEdit = !!profile && can(profile, 'timeline', 'edit');
  const canDelete = !!profile && can(profile, 'timeline', 'delete');

  useEffect(() => {
    if (canView) {
      void loadEntries();
    }
  }, [canView]);

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      const data = await getTimeline();
      setEntries(data);
    } catch (err) {
      logErrorInDev('Failed to load timeline entries', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to load timeline entries.'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaved = () => {
    showToast('Timeline entry saved.');
    void loadEntries();
    setShowModal(false);
    setEditingItem(null);
  };

  const handleEdit = (item: TimelineEntry) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteClick = (item: TimelineEntry) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteTimelineEntry(itemToDelete.id);
      setEntries((prev) => prev.filter((i) => i.id !== itemToDelete.id));
      if (expandedId === itemToDelete.id) setExpandedId(null);
      setShowDeleteModal(false);
      setItemToDelete(null);
      showToast('Timeline entry deleted.');
    } catch (err) {
      logErrorInDev('Failed to delete timeline entry', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete timeline entry.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const filteredEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return entries
      .filter((entry) => {
        const matchesSearch =
          !term ||
          entry.year.toString().includes(term) ||
          entry.title.toLowerCase().includes(term) ||
          (entry.descriptionHtml ?? '').toLowerCase().includes(term);
        const matchesFilter =
          filterActive === 'all' ||
          (filterActive === 'active' && entry.isActive) ||
          (filterActive === 'inactive' && !entry.isActive);
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => a.year - b.year);
  }, [entries, searchTerm, filterActive]);

  const stats = useMemo(() => {
    const active = entries.filter((e) => e.isActive).length;
    const inactive = entries.length - active;
    const years = entries.map((e) => e.year);
    const minYear = years.length ? Math.min(...years) : null;
    const maxYear = years.length ? Math.max(...years) : null;
    const span =
      minYear == null ? '—' : minYear === maxYear ? `${minYear}` : `${minYear}–${maxYear}`;
    return { total: entries.length, active, inactive, span };
  }, [entries]);

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> You do not have permission to view the timeline.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 timeline-mgmt-page">
      <DashboardPageHeader
        title="Timeline Management"
        description="Curate the masjid history timeline shown on the public site — milestones, years, and media."
        icon="bi bi-clock-history"
        actions={
          canEdit ? (
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={() => {
                setEditingItem(null);
                setShowModal(true);
              }}
            >
              <i className="bi bi-plus-circle" aria-hidden />
              Add Timeline Entry
            </button>
          ) : null
        }
      >
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="input-group input-group-sm" style={{ maxWidth: 280 }}>
            <span className="input-group-text bg-light">
              <i className="bi bi-search text-muted" aria-hidden />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search year or title…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="input-group input-group-sm" style={{ maxWidth: 180 }}>
            <span className="input-group-text bg-light">
              <i className="bi bi-funnel text-muted" aria-hidden />
            </span>
            <select
              className="form-select"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as typeof filterActive)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setSearchTerm('');
              setFilterActive('all');
            }}
          >
            <i className="bi bi-arrow-counterclockwise me-1" aria-hidden />
            Reset
          </button>
          <span className="badge rounded-pill text-bg-light border ms-md-auto">
            Showing {filteredEntries.length} of {entries.length}
          </span>
        </div>
      </DashboardPageHeader>

      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="primary"
            title="Total entries"
            value={stats.total}
            icon="bi bi-collection"
          />
        </div>
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="success"
            title="Active"
            value={stats.active}
            icon="bi bi-check-circle"
          />
        </div>
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="neutral"
            title="Inactive"
            value={stats.inactive}
            icon="bi bi-pause-circle"
          />
        </div>
        <div className="col-6 col-lg-3">
          <DashboardStatCard
            variant="panel"
            tone="warning"
            title="Year span"
            value={stats.span}
            icon="bi bi-calendar3"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="timeline-mgmt-loading text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading timeline…</span>
          </div>
          <p className="text-muted mt-3 mb-0">Loading timeline entries…</p>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="timeline-mgmt-empty text-center py-5">
          <div className="timeline-mgmt-empty__icon" aria-hidden>
            <i className="bi bi-clock-history" />
          </div>
          <h3 className="h5 mt-3 mb-2">No timeline entries</h3>
          <p className="text-muted mb-0">
            {searchTerm || filterActive !== 'all'
              ? 'No entries match your search or filter.'
              : 'Add your first milestone to build the public history timeline.'}
          </p>
        </div>
      ) : (
        <ol className="timeline-mgmt-rail list-unstyled mb-0">
          {filteredEntries.map((entry, index) => {
            const imageIds = parseImageIds(entry.imageIdsJson);
            const coverId = imageIds[0] ?? null;
            const isExpanded = expandedId === entry.id;
            const isLast = index === filteredEntries.length - 1;
            const preview = entry.descriptionHtml
              ? stripHtmlPreview(entry.descriptionHtml, isExpanded ? 600 : 140)
              : '';

            return (
              <li
                key={entry.id}
                className={[
                  'timeline-mgmt-item',
                  isExpanded ? 'timeline-mgmt-item--expanded' : '',
                  !entry.isActive ? 'timeline-mgmt-item--inactive' : '',
                  isLast ? 'timeline-mgmt-item--last' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="timeline-mgmt-item__spine" aria-hidden>
                  <span className="timeline-mgmt-item__node">
                    <span className="timeline-mgmt-item__year">{entry.year}</span>
                  </span>
                  {!isLast && <span className="timeline-mgmt-item__line" />}
                </div>

                <article className="timeline-mgmt-card">
                  <div className="timeline-mgmt-card__main">
                    {coverId ? (
                      <div className="timeline-mgmt-card__media">
                        <img
                          src={getImageUrlById(coverId)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {imageIds.length > 1 && (
                          <span className="timeline-mgmt-card__media-count">
                            <i className="bi bi-images me-1" aria-hidden />
                            {imageIds.length}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="timeline-mgmt-card__media timeline-mgmt-card__media--placeholder" aria-hidden>
                        <i className="bi bi-image" />
                      </div>
                    )}

                    <div className="timeline-mgmt-card__body">
                      <button
                        type="button"
                        className="timeline-mgmt-card__hit"
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedId((id) => (id === entry.id ? null : entry.id))}
                      >
                        <div className="timeline-mgmt-card__meta">
                          <span className={`timeline-mgmt-card__status ${entry.isActive ? 'is-active' : 'is-inactive'}`}>
                            {entry.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className="timeline-mgmt-card__expand-hint">
                            {isExpanded ? 'Collapse' : 'Expand'}
                            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ms-1`} aria-hidden />
                          </span>
                        </div>
                        <h3 className="timeline-mgmt-card__title">{entry.title}</h3>
                        {preview ? (
                          <p className="timeline-mgmt-card__preview mb-0">{preview}</p>
                        ) : (
                          <p className="timeline-mgmt-card__preview timeline-mgmt-card__preview--empty mb-0">
                            No description
                          </p>
                        )}
                      </button>

                      {(canEdit || canDelete) && (
                        <div className="timeline-mgmt-card__actions">
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(entry)}
                            >
                              <i className="bi bi-pencil me-1" aria-hidden />
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteClick(entry)}
                            >
                              <i className="bi bi-trash me-1" aria-hidden />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
        </ol>
      )}

      <TimelineModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onTimelineSaved={handleSaved}
        item={editingItem}
        userId={user?.id ?? ''}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        itemName={itemToDelete?.title ?? ''}
        itemType="Timeline Entry"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TimelineManagementPage;
